import { EmailAddress } from '../value-objects/EmailAddress.vo';
import { PhoneNumber } from '../value-objects/PhoneNumber.vo';
import { InvalidContactMessageError } from '../errors/InvalidContactMessageError';

const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 100;
const SUBJECT_MIN_LENGTH = 5;
const SUBJECT_MAX_LENGTH = 150;
const MESSAGE_MIN_LENGTH = 10;
const MESSAGE_MAX_LENGTH = 2000;

export type ContactMessageStatus = 'new' | 'read' | 'archived';

export interface ContactMessageInput {
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
}

export interface ContactMessageProps {
  id: string;
  name: string;
  email: EmailAddress;
  phone: PhoneNumber | null;
  subject: string;
  message: string;
  status: ContactMessageStatus;
  createdAt: string;
}

/**
 * Entity per docs/architecture/ARCHITECTURE-V2-ADDENDUM.md (TASK 1) — has
 * identity (`id`) and a lifecycle (`status` transitions new → read → archived,
 * handled later by the Admin feature), unlike Home's Hero content which is a
 * pure Value Object.
 */
export class ContactMessage {
  public readonly id: string;
  public readonly name: string;
  public readonly email: EmailAddress;
  public readonly phone: PhoneNumber | null;
  public readonly subject: string;
  public readonly message: string;
  public readonly status: ContactMessageStatus;
  public readonly createdAt: string;

  private constructor(props: ContactMessageProps) {
    this.id = props.id;
    this.name = props.name;
    this.email = props.email;
    this.phone = props.phone;
    this.subject = props.subject;
    this.message = props.message;
    this.status = props.status;
    this.createdAt = props.createdAt;
    Object.freeze(this);
  }

  /**
   * Creates a new, not-yet-persisted contact message from raw form input.
   * `id` and `createdAt` are assigned here (client-generated UUID) rather
   * than left to the database, so the entity is fully valid and complete
   * before it ever reaches the Repository.
   */
  public static create(input: ContactMessageInput, id: string, createdAt: string): ContactMessage {
    const name = input.name.trim();
    const subject = input.subject.trim();
    const message = input.message.trim();

    if (name.length < NAME_MIN_LENGTH || name.length > NAME_MAX_LENGTH) {
      throw new InvalidContactMessageError(
        `name must be between ${NAME_MIN_LENGTH} and ${NAME_MAX_LENGTH} characters.`
      );
    }
    if (subject.length < SUBJECT_MIN_LENGTH || subject.length > SUBJECT_MAX_LENGTH) {
      throw new InvalidContactMessageError(
        `subject must be between ${SUBJECT_MIN_LENGTH} and ${SUBJECT_MAX_LENGTH} characters.`
      );
    }
    if (message.length < MESSAGE_MIN_LENGTH || message.length > MESSAGE_MAX_LENGTH) {
      throw new InvalidContactMessageError(
        `message must be between ${MESSAGE_MIN_LENGTH} and ${MESSAGE_MAX_LENGTH} characters.`
      );
    }

    const email = EmailAddress.create(input.email);
    const phone = input.phone && input.phone.trim().length > 0 ? PhoneNumber.create(input.phone) : null;

    return new ContactMessage({
      id,
      name,
      email,
      phone,
      subject,
      message,
      status: 'new',
      createdAt
    });
  }

  /**
   * Reconstructs an entity from persisted data (used by the Repository when
   * reading rows back, e.g. for the future Admin "Contact Messages" list).
   */
  public static reconstitute(props: ContactMessageProps): ContactMessage {
    return new ContactMessage(props);
  }
}
